import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { WriterProfileData } from './types';
import {
  FORMAT_OPTIONS,
  GENRE_OPTIONS,
  STYLE_AXES,
  GOAL_OPTIONS,
  EXPERIENCE_OPTIONS,
  Option,
} from './options';

const cardStyle = { background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' };
const inputClass =
  'w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500';
const inputStyle = { borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' };

export const FieldLabel: React.FC<{ children: React.ReactNode; hint?: string }> = ({
  children,
  hint,
}) => (
  <div className="mb-2">
    <span className="block text-sm font-medium" style={{ color: 'var(--ds-text-primary)' }}>
      {children}
    </span>
    {hint && (
      <span className="block text-xs mt-0.5" style={{ color: 'var(--ds-text-secondary)' }}>
        {hint}
      </span>
    )}
  </div>
);

/** Multi-select chips. */
export const ChipGroup: React.FC<{
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  ariaLabel?: string;
}> = ({ options, selected, onChange, ariaLabel }) => {
  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={ariaLabel}>
      {options.map((o) => {
        const active = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => toggle(o.value)}
            className={`px-3 py-1.5 rounded-full text-sm transition-all ${
              active ? 'bg-primary-500 text-white' : ''
            }`}
            style={!active ? { ...cardStyle, color: 'var(--ds-text-secondary)' } : {}}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

/** Free-text list: type, press Enter (or comma) to add; × to remove. */
export const ChipInput: React.FC<{
  values: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  max?: number;
  inputId?: string;
}> = ({ values, onChange, placeholder, max = 30, inputId }) => {
  const [draft, setDraft] = useState('');
  const { t } = useTranslation();

  const add = () => {
    const v = draft.trim().replace(/,+$/, '').trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setDraft('');
      return;
    }
    if (values.length >= max) return;
    onChange([...values, v.slice(0, 120)]);
    setDraft('');
  };

  return (
    <div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full text-sm"
              style={{ ...cardStyle, color: 'var(--ds-text-primary)' }}
            >
              {v}
              <button
                type="button"
                aria-label={t('writerProfile.remove', 'Remove {{item}}', { item: v })}
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="p-1 rounded-full hover:bg-primary-500/10"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          id={inputId}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
          }}
          onBlur={add}
          placeholder={placeholder}
          className={inputClass}
          style={inputStyle}
          maxLength={120}
        />
        <button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          className="px-4 rounded-xl font-medium bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-white"
        >
          {t('writerProfile.add', 'Add')}
        </button>
      </div>
    </div>
  );
};

/** Single-select cards (goal, experience). */
export const OptionCards: React.FC<{
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  columns?: 1 | 2;
}> = ({ options, value, onChange, columns = 2 }) => {
  const { t } = useTranslation();
  return (
    <div className={`grid gap-2 ${columns === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? '' : o.value)}
            className={`p-3 rounded-xl border text-left transition-all ${
              active ? 'ring-2 ring-primary-500' : ''
            }`}
            style={{
              borderColor: active ? 'var(--ds-accent, #8b5cf6)' : 'var(--ds-border)',
              background: active ? 'rgba(139,92,246,0.1)' : 'var(--ds-bg-card)',
            }}
          >
            <div className="font-medium" style={{ color: 'var(--ds-text-primary)' }}>
              {o.emoji ? `${o.emoji} ` : ''}
              {t(o.key, o.label)}
            </div>
            {o.hint && (
              <div className="text-xs mt-0.5" style={{ color: 'var(--ds-text-secondary)' }}>
                {t(o.hintKey || '', o.hint)}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

/** A row of "this ↔ that" toggles. Unset axes are simply omitted from the profile. */
export const StyleAxes: React.FC<{
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const set = (axis: string, v: string) => {
    const next = { ...value };
    if (next[axis] === v) delete next[axis];
    else next[axis] = v;
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {STYLE_AXES.map((axis) => (
        <div key={axis.key}>
          <div className="text-xs mb-1" style={{ color: 'var(--ds-text-secondary)' }}>
            {t(axis.labelKey, axis.label)}
          </div>
          <div
            className="grid grid-cols-2 gap-2"
            role="group"
            aria-label={t(axis.labelKey, axis.label)}
          >
            {[axis.left, axis.right].map((side) => {
              const active = value[axis.key] === side.value;
              return (
                <button
                  key={side.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => set(axis.key, side.value)}
                  className={`py-2 px-3 rounded-xl border text-sm transition-colors ${
                    active ? 'bg-primary-500 border-primary-500 text-white' : ''
                  }`}
                  style={!active ? { ...cardStyle, color: 'var(--ds-text-primary)' } : {}}
                >
                  {t(side.key, side.label)}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export const TextArea: React.FC<{
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  rows?: number;
  maxLength: number;
}> = ({ id, value, onChange, placeholder, rows = 4, maxLength }) => (
  <div>
    <textarea
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className={`${inputClass} resize-y`}
      style={inputStyle}
    />
    <div className="text-right text-[11px] mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
      {value.length}/{maxLength}
    </div>
  </div>
);

// ─── Sections (shared by the onboarding wizard and the Settings page) ────────

type SectionProps = {
  data: WriterProfileData;
  onChange: (patch: Partial<WriterProfileData>) => void;
};

export const FormatsSection: React.FC<SectionProps> = ({ data, onChange }) => {
  const { t } = useTranslation();
  return (
    <div>
      <FieldLabel hint={t('writerProfile.formatsHint', 'Pick everything that applies.')}>
        {t('writerProfile.formatsLabel', 'What do you write?')}
      </FieldLabel>
      <ChipGroup
        ariaLabel={t('writerProfile.formatsLabel', 'What do you write?')}
        options={FORMAT_OPTIONS.map((o) => ({
          value: o.value,
          label: `${o.emoji} ${t(o.key, o.label)}`,
        }))}
        selected={data.formats}
        onChange={(formats) => onChange({ formats })}
      />
    </div>
  );
};

export const GenresSection: React.FC<SectionProps> = ({ data, onChange }) => {
  const { t } = useTranslation();
  return (
    <div>
      <FieldLabel hint={t('writerProfile.genresHint', 'The worlds you keep coming back to.')}>
        {t('writerProfile.genresLabel', 'Genres you love')}
      </FieldLabel>
      <ChipGroup
        ariaLabel={t('writerProfile.genresLabel', 'Genres you love')}
        options={GENRE_OPTIONS.map((g) => ({ value: g.value, label: t(g.key, g.label) }))}
        selected={data.genres}
        onChange={(genres) => onChange({ genres })}
      />
    </div>
  );
};

export const InfluencesSection: React.FC<SectionProps> = ({ data, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <div>
        <FieldLabel
          hint={t('writerProfile.favoriteBooksHint', 'Titles that made you want to write.')}
        >
          {t('writerProfile.favoriteBooksLabel', 'Favorite books')}
        </FieldLabel>
        <ChipInput
          inputId="favoriteBooks"
          values={data.favoriteBooks}
          onChange={(favoriteBooks) => onChange({ favoriteBooks })}
          placeholder={t(
            'writerProfile.favoriteBooksPlaceholder',
            'e.g. Beloved, Dune, The Hobbit',
          )}
        />
      </div>
      <div>
        <FieldLabel
          hint={t(
            'writerProfile.influencesHint',
            'Authors, films, playwrights, shows — anything that shaped your taste.',
          )}
        >
          {t('writerProfile.influencesLabel', 'Your influences')}
        </FieldLabel>
        <ChipInput
          inputId="influences"
          values={data.influences}
          onChange={(influences) => onChange({ influences })}
          placeholder={t(
            'writerProfile.influencesPlaceholder',
            'e.g. Ursula K. Le Guin, Coen brothers, Fleabag',
          )}
        />
      </div>
    </div>
  );
};

export const StyleSection: React.FC<SectionProps & { showSample?: boolean }> = ({
  data,
  onChange,
  showSample = true,
}) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <div>
        <FieldLabel hint={t('writerProfile.styleHint', 'Lean one way or leave it blank.')}>
          {t('writerProfile.styleLabel', 'Your style')}
        </FieldLabel>
        <StyleAxes value={data.styleTraits} onChange={(styleTraits) => onChange({ styleTraits })} />
      </div>
      <div>
        <FieldLabel
          hint={t('writerProfile.voiceHint', 'How would a friend describe the way you write?')}
        >
          {t('writerProfile.voiceLabel', 'Your voice, in your words')}
        </FieldLabel>
        <TextArea
          id="voiceDescription"
          value={data.voiceDescription}
          onChange={(voiceDescription) => onChange({ voiceDescription })}
          placeholder={t(
            'writerProfile.voicePlaceholder',
            'e.g. Short sentences, dry humor, a lot left unsaid.',
          )}
          rows={3}
          maxLength={1000}
        />
      </div>
      {showSample && (
        <div>
          <FieldLabel
            hint={t(
              'writerProfile.sampleHint',
              'Optional. A paragraph or two of your own writing helps the AI match your rhythm.',
            )}
          >
            {t('writerProfile.sampleLabel', 'A sample of your writing')}
          </FieldLabel>
          <TextArea
            id="writingSample"
            value={data.writingSample}
            onChange={(writingSample) => onChange({ writingSample })}
            placeholder={t('writerProfile.samplePlaceholder', 'Paste a short passage…')}
            rows={6}
            maxLength={4000}
          />
        </div>
      )}
    </div>
  );
};

export const GoalSection: React.FC<SectionProps> = ({ data, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <div>
        <FieldLabel>{t('writerProfile.goalLabel', 'What are you here to do?')}</FieldLabel>
        <OptionCards
          options={GOAL_OPTIONS}
          value={data.goal}
          onChange={(goal) => onChange({ goal })}
        />
      </div>
      <div>
        <FieldLabel>{t('writerProfile.experienceLabel', 'Where are you as a writer?')}</FieldLabel>
        <OptionCards
          options={EXPERIENCE_OPTIONS}
          value={data.experienceLevel}
          onChange={(experienceLevel) => onChange({ experienceLevel })}
        />
      </div>
    </div>
  );
};
